import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { cn } from '@/utils/classname';

import { QueryBuilderSidebar } from './QueryBuilderSidebar';

/**
 * QueryBuilderSidebarWrapper - Collapsible sidebar container with toggle button
 *
 * Wraps QueryBuilderSidebar with collapse/expand functionality:
 * - Sidebar animates between 400px (expanded) and 0px (collapsed)
 * - Toggle button positioned at sidebar edge
 * - Smooth transitions for width and button position
 * - Overflow hidden prevents content reflow during animation
 */
export const QueryBuilderSidebarWrapper = () => {
  const { sidebarCollapsed, setSidebarCollapsed } = useQueryBuilder();
  const expanded = !sidebarCollapsed;

  return (
    <div
      className={cn(
        'border-chariot-stroke relative border-l transition-all duration-200',
        expanded ? 'border-r' : ''
      )}
      style={{
        width: expanded ? '400px' : 0,
      }}
      data-collapsed={sidebarCollapsed}
    >
      {/* Sidebar Content - overflow hidden to prevent reflow during animation */}
      {expanded && (
        <div className="h-full overflow-hidden">
          <QueryBuilderSidebar />
        </div>
      )}

      {/* Toggle Button - positioned at right edge of sidebar, pushed outside with translateX */}
      <button
        aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        className="border-chariot-stroke bg-chariot-layer1 absolute top-4 right-0 z-50 flex h-10 w-7 items-center justify-center rounded-r-full border border-l-0 shadow transition-all duration-200"
        style={{
          transform: 'translateX(calc(100%))',
          padding: 0,
        }}
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        tabIndex={0}
        data-testid="sidebar-toggle"
      >
        <span
          className={cn(
            'text-chariot-stroke transition-transform duration-300'
          )}
        >
          {expanded ? (
            <ChevronLeftIcon className="size-5" />
          ) : (
            <ChevronRightIcon className="size-5" />
          )}
        </span>
      </button>
    </div>
  );
};
